import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

document.addEventListener('alpine:init', () => {
    Alpine.data('locationPicker', (params) => window.locationPicker(params));
});

if (window.Alpine) {
    window.Alpine.data('locationPicker', (params) => window.locationPicker(params));
}

window.locationPicker = ({ locations, selected, statePath }) => ({
    // --- reactive state ---
    locations: Array.isArray(locations) ? locations : Object.values(locations ?? {}),
    filteredLocations: [],
    selected,
    statePath,
    map: null,
    markers: [],
    userMarker: null,
    addressQuery: '',
    addressResults: [],
    isMobile: window.innerWidth < 640,
    popupOffset: window.innerWidth < 640 ? -8 : -12, // centralized popup offset

    // --- Lazy init (ensures container visible) ---
    initDeferred() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    observer.disconnect();
                    this.init();
                }
            });
        }, { threshold: 0.2 });
        observer.observe(this.$el);
    },

    // --- Lifecycle ---
    init() {
        this.filteredLocations = this.locations;

        this.$nextTick(() => {
            this.initializeMap();

            setTimeout(() => this.locateUser(), 1000);

            window.addEventListener('resize', () => {
                const nowMobile = window.innerWidth < 640;
                if (nowMobile !== this.isMobile) {
                    this.isMobile = nowMobile;
                    this.popupOffset = nowMobile ? -8 : -12;
                    this.initializeMap();
                } else {
                    this.map.invalidateSize();
                }
            });
        });

        if (this.selected) {
            this.$nextTick(() => this.selectLocation(this.selected, 13));
        }
    },

    // --- Map setup ---
    initializeMap() {
        const isMobile = window.innerWidth < 640;
        const mapId = isMobile ? '#map-mobile' : '#map-desktop';
        const mapEl = this.$el.querySelector(mapId);

        if (!mapEl) return;

        if (this.map) {
            this.map.remove();
            this.map = null;
        }

        this.map = L.map(mapEl).setView([51.1657, 10.4515], 6);
        mapEl._leaflet_map_instance = this.map;

        const stadiaApiKey =
            window.appConfig?.stadiaApiKey ||
            import.meta.env.VITE_STADIA_KEY ||
            '';

        const baseUrl = 'https://tiles-eu.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png';
        const tileUrl = stadiaApiKey
            ? `${baseUrl}?language=de&api_key=${stadiaApiKey}`
            : `${baseUrl}?language=de`;

        L.tileLayer(tileUrl, {
            maxZoom: 19,
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | ' +
                '<a href="https://stadiamaps.com/">Stadia Maps</a>',
        }).addTo(this.map);

        this.renderMarkers();
        setTimeout(() => this.map.invalidateSize(), 400);
    },

    // --- Manual selection (list or marker) ---
    selectLocation(id, zoomOverride = null) {
        const loc = this.locations.find(l => l.id === id);
        if (!loc || !this.map) return;

        this.selected = id;

        if (this.statePath && this.$wire) {
            this.$wire.set(this.statePath, id);
        }

        const marker = this.markers.find(m => {
            const { lat, lng } = m.getLatLng();
            return Math.abs(lat - loc.lat) < 0.0001 && Math.abs(lng - loc.lng) < 0.0001;
        });

        // Calculate distance dynamically if user located
        if (!loc.distance && this.userMarker) {
            const { lat, lng } = this.userMarker.getLatLng();
            loc.distance = this.haversine(lat, lng, loc.lat, loc.lng);
        }

        const zoom = zoomOverride ?? 15;
        const offsetY = -60;
        const point = this.map.project([loc.lat, loc.lng], zoom);
        const shiftedLatLng = this.map.unproject(L.point(point.x, point.y - offsetY), zoom);

        this.map.flyTo(shiftedLatLng, zoom, { animate: true, duration: 0.8 });

        const popupHtml = `
            <b>${loc.name}</b><br>
            ${loc.address ?? ''}<br>
            ${loc.distance
            ? `<small style="color:#666;">${loc.distance.toFixed(1)} km entfernt</small>`
            : ''}
        `;

        (marker
                ? marker.bindPopup(popupHtml, { offset: [0, this.popupOffset] })
                : L.popup({ offset: [0, this.popupOffset] })
                .setLatLng([loc.lat, loc.lng])
                .setContent(popupHtml)
        ).openPopup();
    },

    // --- Render all markers ---
    renderMarkers() {
        this.markers.forEach(m => this.map.removeLayer(m));
        this.markers = [];

        const customMarker = L.divIcon({
            className: 'custom-marker',
            html: `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                     fill="rgb(119, 41, 190)" width="28" height="28">
                    <path fill-rule="evenodd"
                          d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742
                             19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0
                             c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742Z
                             M12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd"/>
                </svg>
            `,
            iconSize: [28, 28],
            iconAnchor: [14, 28],
            popupAnchor: [0, this.popupOffset],
        });

        if (!Array.isArray(this.filteredLocations)) return;

        this.filteredLocations.forEach((loc, idx) => {
            if (!loc.lat || !loc.lng) return;

            const marker = L.marker([loc.lat, loc.lng], { icon: customMarker })
            .addTo(this.map)
            .bindPopup(`
                    <b>${idx + 1}. ${loc.name}</b><br>
                    ${loc.address ?? ''}<br>
                    <small>${loc.distance ? loc.distance.toFixed(1) + ' km entfernt' : ''}</small>
                `, { offset: [0, this.popupOffset] })
            .on('click', () => this.selectLocation(loc.id));

            this.markers.push(marker);
        });

        if (this.markers.length > 0) {
            const group = L.featureGroup(this.markers);
            this.map.fitBounds(group.getBounds().pad(0.3));
        }
    },

    // --- Haversine (distance km) ---
    haversine(lat1, lon1, lat2, lon2) {
        const R = 6371;
        const toRad = deg => deg * (Math.PI / 180);
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },

    // --- Locate user via browser API ---
    locateUser() {
        if (!navigator.geolocation) {
            console.warn('Geolocation not supported.');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;

                const userIcon = L.divIcon({
                    className: 'user-marker',
                    html: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="rgb(119, 41, 190)">
                             <circle cx="7" cy="7" r="6" opacity="0.25"/>
                             <circle cx="7" cy="7" r="3"/>
                           </svg>`,
                    iconSize: [14, 14],
                    iconAnchor: [7, 7],
                });

                if (this.userMarker) this.map.removeLayer(this.userMarker);
                this.userMarker = L.marker([lat, lng], { icon: userIcon }).addTo(this.map);

                this.filteredLocations = this.locations.map(l => ({
                    ...l,
                    distance: this.haversine(lat, lng, l.lat, l.lng),
                })).sort((a, b) => a.distance - b.distance);

                this.renderMarkers();
                this.selectNearestLocation(lat, lng);
            },
            (err) => console.warn('Geolocation error:', err.message),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
    },

    // --- Find nearest and center map softly ---
    selectNearestLocation(lat, lng) {
        if (!this.locations?.length) return;

        let nearest = null;
        let minDist = Infinity;

        this.locations.forEach((loc) => {
            const dist = this.haversine(lat, lng, loc.lat, loc.lng);
            if (dist < minDist) {
                minDist = dist;
                nearest = loc;
            }
        });

        if (nearest) {
            this.selectLocation(nearest.id, 11);
        }
    },
});

// --- Fix map sizing after Livewire renders ---
document.addEventListener('livewire:load', () => {
    Livewire.hook('message.processed', () => {
        document.querySelectorAll('[x-data*=locationPicker]').forEach((el) => {
            const mapContainer = el.querySelector('.map-container');
            if (mapContainer && mapContainer._leaflet_map_instance) {
                mapContainer._leaflet_map_instance.invalidateSize();
            }
        });
    });
});
